import React, { useEffect, useState } from 'react';
import { Loader2, MonitorSmartphone } from 'lucide-react';
import apiClient from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AuthShell, AUTH_TITLE_CLASS } from '@/components/auth/AuthShell';

// ---------------------------------------------------------------------------
// DesktopAuthConsentPage — browser-handoff sign-in for the Cadence Mac (and
// future desktop) apps. The Mac launches an OS-managed browser session
// pointing at /oauth/desktop?session=<id>&state=<nonce>. We get rendered
// here after the user has already cleared the normal login + MFA on this
// page (the protected-route gate handles that). The user confirms "yes,
// sign me in to my desktop app", we POST /auth/desktop/authorize to mint
// a single-use code, and we redirect the browser to a `cadence://`
// callback URL. The Mac's URL handler picks that up and exchanges the
// code for tokens.
//
// Why not auto-approve? Two reasons:
//   * The user could be on a friend's machine, signed in to *their* Cadence
//     account, and a phishy link asks them to "sign in" to a desktop app
//     they don't own. The consent step puts a human in the loop.
//   * Surfaces what just happened in plain English. Browser handoffs are
//     opaque otherwise — the user sees a flash of a page, then a different
//     app opens, and they're not sure what happened.
// ---------------------------------------------------------------------------

interface AuthorizeResponse {
  code: string;
  state: string;
  callbackUrl: string;
}

// sessionStorage key — survives every URL-replaceState clobber the SPA does
// on auth state transitions. The values are bound to the current tab only,
// so a second sign-in attempt in another tab gets its own slot.
const STASH_KEY = 'cadence:desktop-auth-session';

interface StashedSession { sessionId: string; state: string }

/// Read session+state from the URL on first mount, persist to
/// sessionStorage so we don't lose them if some other component
/// replaceState's the URL. On subsequent renders, prefer the URL if it
/// still has them; otherwise fall back to the stash. This mirrors the
/// "URL clobbered to /" failure mode that the equivalent MCP OAuth page
/// hit in April 2026 — params were correct at network-load time but
/// undefined by the time the React component's useEffect fired.
function readSession(): StashedSession {
  const params = new URLSearchParams(window.location.search);
  const urlSession = params.get('session') || '';
  const urlState = params.get('state') || '';
  if (urlSession) {
    // First-time read with a valid URL — persist for later renders.
    try {
      sessionStorage.setItem(STASH_KEY, JSON.stringify({ sessionId: urlSession, state: urlState }));
    } catch { /* private-browsing: best-effort, URL is still good for this render */ }
    return { sessionId: urlSession, state: urlState };
  }
  try {
    const raw = sessionStorage.getItem(STASH_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as StashedSession;
      if (parsed.sessionId) return parsed;
    }
  } catch { /* malformed — fall through to empty */ }
  return { sessionId: '', state: '' };
}

export default function DesktopAuthConsentPage() {
  const { user } = useAuth();
  const { sessionId } = readSession();

  const [phase, setPhase] = useState<'consent' | 'submitting' | 'redirecting' | 'error'>('consent');
  const [error, setError] = useState<string | null>(null);

  // If neither the URL nor the stash has a session id we can't proceed —
  // the Mac generates and supplies that. Bail out with a clear message
  // rather than a fake-feeling consent screen.
  useEffect(() => {
    if (!sessionId) {
      const observedUrl = window.location.href;
      const observedSearch = window.location.search || '(empty)';
      setError(
        "This page is opened by the Cadence app as part of signing you in. " +
        "Open Cadence and tap Sign in there — don't navigate to this URL by hand. " +
        `\n\nDiagnostic: URL was ${observedUrl}. Query string was ${observedSearch}.`
      );
      setPhase('error');
    }
  }, [sessionId]);

  const approve = async () => {
    setPhase('submitting');
    setError(null);
    try {
      const { data } = await apiClient.post<AuthorizeResponse>('/auth/desktop/authorize', {
        sessionId,
      });
      // Authorize succeeded — clear the stash so a future visit to this
      // page (e.g. a new tab from a second Mac sign-in) doesn't pick up
      // this attempt's now-consumed session id.
      try { sessionStorage.removeItem(STASH_KEY); } catch { /* private-browsing — best-effort */ }
      setPhase('redirecting');
      window.setTimeout(() => {
        window.location.assign(data.callbackUrl);
      }, 400);
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Could not authorise the Cadence app.';
      setError(typeof msg === 'string' ? msg : 'Could not authorise the Cadence app.');
      setPhase('error');
    }
  };

  const cancel = () => {
    // Drop the user on the dashboard — they're signed in to the web; if
    // they cancelled by mistake they can re-open the Mac app to retry.
    window.location.assign('/');
  };

  if (phase === 'redirecting') {
    return (
      <AuthShell width="wide">
        <Card>
          <CardContent className="space-y-4 p-8 text-center">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
            <h1 className={AUTH_TITLE_CLASS}>Signing you in…</h1>
            <p className="text-sm text-foreground/70">
              Cadence should reopen in a moment. You can close this tab.
            </p>
            <p className="text-xs text-muted-foreground">
              Nothing happened?{' '}
              <button onClick={() => approve()} className="underline">Try again</button>
            </p>
          </CardContent>
        </Card>
      </AuthShell>
    );
  }

  if (phase === 'error') {
    return (
      <AuthShell width="wide">
        <Card>
          <CardContent className="space-y-4 p-8">
            <h1 className={AUTH_TITLE_CLASS}>We couldn't sign you in to the Cadence app</h1>
            <Alert variant="error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <Button onClick={cancel} variant="outline" className="w-full">
              Back to Cadence
            </Button>
          </CardContent>
        </Card>
      </AuthShell>
    );
  }

  return (
    <AuthShell width="wide">
      <Card>
        <CardContent className="space-y-6 p-8">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/15">
              <MonitorSmartphone className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className={AUTH_TITLE_CLASS}>Sign in to Cadence on this device?</h1>
              <p className="text-xs text-muted-foreground">Signed in as {user?.email ?? 'your account'}</p>
            </div>
          </div>

          <p className="text-sm text-foreground/75">
            You're about to give the Cadence app access to your account.
            It will be able to do anything you can do on the web —
            view and update your plan, work items, calendar, drivers, and decisions.
          </p>

          <div className="space-y-3">
            <Button
              onClick={approve}
              disabled={phase === 'submitting' || !sessionId}
              className="w-full"
            >
              {phase === 'submitting' ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Approving…
                </>
              ) : (
                'Allow'
              )}
            </Button>
            <Button
              onClick={cancel}
              variant="outline"
              disabled={phase === 'submitting'}
              className="w-full"
            >
              Cancel
            </Button>
          </div>

          <p className="text-xs leading-relaxed text-muted-foreground">
            If you didn't just tap "Sign in" in the Cadence app, don't approve this —
            cancel and tell us at the in-app "Report a problem" button.
          </p>
        </CardContent>
      </Card>
    </AuthShell>
  );
}
